import React from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { twMerge } from 'tailwind-merge'
import { PrimaryButton } from 'shared/components/button'
import ErrorMessageRhf from 'shared/components/error-message-rhf'
import FormInputRhf from 'shared/components/form-input-rhf'
import { Recaptcha } from 'shared/components/recaptcha'
import { GreenCircleSuccessIcon } from 'shared/icons/green-circle-succes-icon'
import { AuthFormHeader } from 'modules/authentication/components/auth-form-header'
import { AuthFormLayout } from 'modules/authentication/components/auth-form-layout'
import { DEFAULT_AUTH_FORM_CLASSES } from 'modules/authentication/const/default-classes'
import { useRestorePasswordForm } from '../hooks/use-restore-password-form'

interface RestorePasswordFormDashboardProps {
  onSuccess: () => void
  isSent: boolean
}

export const RestorePasswordFormDashboard = ({
  onSuccess,
  isSent,
}: RestorePasswordFormDashboardProps) => {
  const { t } = useTranslation()
  const router = useRouter()

  const {
    handleSubmit,
    control,
    setValue,
    errors,
    isLoading,
    onSubmit,
    isRecaptchaError,
    toggleRecaptchaError,
    recaptchaRef,
  } = useRestorePasswordForm({
    onSuccess,
  })

  return (
    <>
      {isSent ? (
        <>
          <AuthFormHeader
            title={t('resetting.check_email.title')}
            logo={
              <GreenCircleSuccessIcon className="w-[45px] max-w-[45px] md:w-[53px] md:max-w-[53px]" />
            }
            description={t('resetting.check_email.description')}
          />
          <Link
            className={twMerge(
              DEFAULT_AUTH_FORM_CLASSES.BUTTON,
              'border border-gray-600 bg-white p-3 text-center text-gray-900 transition-colors hover:bg-gray-200/20',
            )}
            href="/login"
          >
            {t('resetting.check_email.back_to_login')}
          </Link>
        </>
      ) : (
        <>
          <AuthFormHeader title={t('resetting.request.title')} />
          <form onSubmit={handleSubmit(onSubmit)}>
            <AuthFormLayout
              body={
                <>
                  <FormInputRhf
                    type="email"
                    name="email"
                    className={DEFAULT_AUTH_FORM_CLASSES.INPUT_WRAPPER}
                    inputClassName={DEFAULT_AUTH_FORM_CLASSES.INPUT}
                    label={t('authentication.email')}
                    control={control}
                    setValue={setValue}
                    maxLength={180}
                    rules={{ required: t('login.validation.email') }}
                    error={errors.email?.message}
                    autoComplete="email"
                    autoCapitalize="none"
                    spellCheck={false}
                  />
                  {!isRecaptchaError && (
                    <Controller
                      control={control}
                      rules={{ required: t('user.registration.captcha.verification') }}
                      name="token"
                      render={({ field, formState: { errors } }) => (
                        <div>
                          <Recaptcha
                            key={router.locale}
                            ref={recaptchaRef}
                            onTokenChange={field.onChange}
                            onError={toggleRecaptchaError}
                          />
                          <ErrorMessageRhf errors={errors} fieldName="token" />
                        </div>
                      )}
                    />
                  )}
                  <ErrorMessageRhf errors={errors} fieldName="root" />
                </>
              }
              footer={
                <>
                  <PrimaryButton
                    isLoading={isLoading}
                    type="submit"
                    className={DEFAULT_AUTH_FORM_CLASSES.BUTTON}
                  >
                    {t('resetting.request.submit')}
                  </PrimaryButton>
                </>
              }
            />
          </form>
        </>
      )}
    </>
  )
}
